import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router";
import {
  Calendar,
  MapPin,
  Users,
  Star,
  Bell,
  BellOff,
  User,
  Loader2,
} from "lucide-react";
import { motion } from "motion/react";
import { useAppStore } from "../store/useAppStore";
import { sessionsService } from "../services/sessionsService";
import type { EventSession } from "../types";
import { PageShell } from "../components/PageShell";
import { PageHeader } from "../components/PageHeader";
import { GlassCard } from "../components/GlassCard";
import { Button } from "../components/Button";
import { RichText } from "../components/RichText";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { intlLocale } from "../i18n";
import { formatVenueDateTime } from "../utils/venueDateTime";
import { htmlToPlainText } from "../utils/safeHtml";
import { programmeTagClass } from "../utils/programmeTags";

function formatDateTime(iso: string, locale: string, timeZone?: string): string {
  return formatVenueDateTime(iso, intlLocale(locale), timeZone ?? "Europe/Moscow");
}

function formatDuration(start: string, end: string, t: (key: string, options?: Record<string, unknown>) => string): string {
  const minutes = Math.round(
    (new Date(end).getTime() - new Date(start).getTime()) / 60000,
  );
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0) return t("visitor.duration_hours_minutes", { hours, minutes: mins });
  return t("visitor.duration_minutes", { minutes: mins });
}

export function SessionDetailPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, selectedEventId } = useAppStore();
  const [session, setSession] = useState<EventSession | undefined>();
  const [isFavorite, setIsFavorite] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reminderEnabled, setReminderEnabled] = useState(false);

  const eventId = selectedEventId ?? session?.eventId ?? "";

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const found = await sessionsService.getById(eventId || "evt-1", id);
        if (!cancelled && found) {
          setSession(found);
          const fav = await sessionsService.isFavorite(found.eventId, user.id, found.id);
          if (!cancelled) setIsFavorite(fav);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, eventId, user.id]);

  if (loading) {
    return (
      <PageShell>
        <div className="pt-20 flex justify-center text-gray-400">
          <Loader2 size={28} className="animate-spin" />
        </div>
      </PageShell>
    );
  }

  if (!session) {
    return (
      <PageShell>
        <div className="pt-20 text-center">
          <div className="text-gray-500 mb-4">{t("schedule.not_found")}</div>
          <Button variant="secondary" onClick={() => navigate(-1)}>
            {t("common.back")}
          </Button>
        </div>
      </PageShell>
    );
  }

  const handleToggleFavorite = async () => {
    if (isFavorite) {
      await sessionsService.removeFromFavorites(user.id, session.id, session.eventId);
      setIsFavorite(false);
      toast.success(t("schedule.fav_removed"));
    } else {
      await sessionsService.addToFavorites(user.id, session.id, session.eventId);
      setIsFavorite(true);
      toast.success(t("schedule.fav_added"));
    }
  };

  const handleToggleReminder = async () => {
    const next = !reminderEnabled;
    try {
      await sessionsService.setFavoriteNotification(
        user.id,
        session.eventId,
        session.id,
        next,
      );
      setReminderEnabled(next);
      toast.success(next ? t("schedule.reminder_on") : t("visitor.reminder_cancelled"));
    } catch {
      toast.error(t("visitor.reminder_failed"));
    }
  };

  return (
    <PageShell noPadding>
      <PageHeader
        title={t("schedule.title")}
        back
        backTo="/schedule"
        right={
          <button
            type="button"
            onClick={handleToggleFavorite}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label={isFavorite ? t("schedule.fav_removed") : t("schedule.fav_added")}
          >
            <Star
              size={20}
              className={
                isFavorite ? "text-amber-500 fill-amber-500" : "text-gray-400"
              }
            />
          </button>
        }
      />

      <div className="px-4 pt-4 pb-24 space-y-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <GlassCard variant="elevated" padding="lg">
            <div className="mb-3 flex flex-wrap gap-1.5">
              {(session.tags?.length
                ? session.tags
                : [
                    t(`visitor.session_type_${session.type}`, {
                      defaultValue: session.type,
                    }),
                  ]
              ).map((tag) => (
                <span
                  key={tag}
                  className={`inline-block px-2.5 py-1 rounded-lg text-[12px] font-medium ${
                    session.tags?.length
                      ? programmeTagClass(tag)
                      : "bg-indigo-100 text-indigo-700"
                  }`}
                >
                  {tag}
                </span>
              ))}
            </div>

            <h2 className="text-[20px] text-gray-900 mb-3">{htmlToPlainText(session.title)}</h2>

            {session.description?.trim() ? (
              <RichText
                html={session.description}
                className="text-[14px] text-gray-600 leading-relaxed mb-4"
              />
            ) : null}

            <div className="space-y-2">
              <div className="flex items-start gap-2 text-[13px]">
                <Calendar size={16} className="text-indigo-500 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="text-gray-900 font-medium">
                    {formatDateTime(session.startTime, i18n.language, session.venueTimeZone)}
                  </div>
                  <div className="text-gray-500">
                    {t("visitor.duration", { duration: formatDuration(session.startTime, session.endTime, t) })}
                  </div>
                </div>
              </div>

              {session.location && (
                <div className="flex items-center gap-2 text-[13px] text-gray-600">
                  <MapPin size={16} className="text-indigo-500 flex-shrink-0" />
                  <span>{session.location}</span>
                </div>
              )}
            </div>
          </GlassCard>
        </motion.div>

        {session.speakers.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.4 }}
          >
            <GlassCard variant="default">
              <div className="flex items-center gap-2 mb-3">
                <Users size={16} className="text-indigo-500" />
                <h3 className="text-[14px] text-gray-900">
                  {t("schedule.speakers", { n: session.speakers.length })}
                </h3>
              </div>

              <div className="space-y-3">
                {session.speakers.map((speaker) => (
                  <div key={speaker.id} className="flex items-start gap-3">
                    {speaker.photo ? (
                      <img
                        src={speaker.photo}
                        alt={speaker.fullName}
                        className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center flex-shrink-0">
                        <User size={20} className="text-indigo-600" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-[14px] text-gray-900 font-medium">
                        {speaker.fullName}
                      </div>
                      <div className="text-[12px] text-gray-500">
                        {speaker.position}
                        {speaker.company && ` · ${speaker.company}`}
                      </div>
                      {speaker.bio && (
                        <p className="text-[12px] text-gray-600 mt-1 line-clamp-2">
                          {speaker.bio}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </GlassCard>
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.4 }}
          className="space-y-2"
        >
          <Button
            variant={isFavorite ? "secondary" : "primary"}
            onClick={handleToggleFavorite}
            className="w-full"
          >
            <Star size={18} />
            {isFavorite ? t("schedule.in_favorites") : t("schedule.add_favorite")}
          </Button>

          <Button
            variant={reminderEnabled ? "secondary" : "primary"}
            onClick={handleToggleReminder}
            className="w-full"
          >
            {reminderEnabled ? <BellOff size={18} /> : <Bell size={18} />}
            {reminderEnabled ? t("schedule.reminder_on") : t("schedule.remind_me")}
          </Button>
        </motion.div>
      </div>
    </PageShell>
  );
}
